import { CopyFilled, CopyTwoTone } from '@ant-design/icons'
import { Intent } from '@blueprintjs/core'
import { Spin } from 'antd'
import Toaster from 'components/Toaster'
import React, { useState } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators, Dispatch } from 'redux'
import { RootState } from 'store/rootReducer'
import {
  createLoadingSelector,
  createSuccessSelector,
} from 'store/status/selectors'
import { archiveEncordAssets } from 'store/videoSegments/actionCreators'
import { archiveEncordAssetsFlow } from 'store/videoSegments/actions'

type Props = StoreProps<typeof mapStateToProps, typeof mapDispatchToProps>

const ArchiveEncordAssets: React.FC<Props> = ({
  archiveEncordAssets,
  isArchivingEncordAssets,
  isEncordAssetsArchived,
}) => {
  const [projectId, setProjectId] = useState('')
  const [clickCopy, setClickCopy] = useState(false)
  const email =
    process.env.REACT_APP_ENCORD_SERVICE_ADMIN_EMAIL || 'Email not found'

  const handleArchive = () => {
    if (projectId.trim()) {
      const params = { projectId: projectId.trim() }
      archiveEncordAssets(params)
    }
  }

  const renderCopyButton = () => {
    return !clickCopy ? (
      <CopyTwoTone
        style={{ marginLeft: '5px' }}
        onClick={e => {
          e.stopPropagation()
          toggleAfterCopy()
        }}></CopyTwoTone>
    ) : (
      <CopyFilled style={{ marginLeft: '5px' }}> </CopyFilled>
    )
  }

  const toggleAfterCopy = () => {
    setClickCopy(true)
    navigator.clipboard.writeText(email)
    Toaster.show({
      icon: 'tick',
      intent: Intent.SUCCESS,
      message: 'Copied to Clipboard!',
    })

    setTimeout(() => {
      setClickCopy(false)
    }, 2000)
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '2rem',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        maxWidth: '800px',
        width: '100%',
        margin: '0 auto',
        gap: '1rem',
      }}>
      <div style={{ width: '100%' }}>
        <label
          htmlFor="projectId"
          style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: '500',
            fontSize: '16px',
          }}>
          Archive/Advance Encord Assets
        </label>
        <p
          style={{
            fontSize: '14px',
            color: '#666',
            marginTop: '0.5rem',
            marginBottom: '1rem',
          }}>
          <b>Step 1:</b> Please add this user email:{' '}
          <span
            style={{
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              backgroundColor: '#f5f5f5',
              padding: '0.1rem 0.5rem',
              borderRadius: '4px',
              margin: '0 0.25rem',
              cursor: 'pointer',
              border: '1px solid #d9d9d9',
            }}
            onClick={toggleAfterCopy}>
            <span style={{ fontWeight: '500' }}>{email}</span>
            {renderCopyButton()}
          </span>{' '}
          to the project in Encord dashboard and give it &quot;Team
          Manager&quot; role.
        </p>
        <p
          style={{
            fontSize: '14px',
            color: '#666',
            marginTop: '1rem',
            marginBottom: '0.5rem',
          }}>
          <b>Step 2:</b> Please enter the Encord project ID below. Assets in the
          &quot;Agent 1&quot; stage with the &quot;Skipped&quot; classification
          will be archived, while all others will advance to the next stage.
        </p>
        <input
          id="projectId"
          type="text"
          value={projectId}
          onChange={e => setProjectId(e.target.value)}
          placeholder="Enter project ID"
          style={{
            width: '100%',
            padding: '0.75rem',
            borderRadius: '4px',
            border: '1px solid #d9d9d9',
            fontSize: '16px',
            outline: 'none',
            transition: 'border-color 0.3s ease',
            boxSizing: 'border-box',
          }}
          onFocus={e => (e.target.style.borderColor = '#40a9ff')}
          onBlur={e => (e.target.style.borderColor = '#d9d9d9')}
        />
      </div>

      <button
        type="button"
        onClick={handleArchive}
        disabled={!projectId.trim() || isArchivingEncordAssets}
        style={{
          backgroundColor:
            projectId.trim() && !isArchivingEncordAssets
              ? '#1890ff'
              : '#d9d9d9',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          padding: '0.75rem 1.5rem',
          cursor:
            projectId.trim() && !isArchivingEncordAssets
              ? 'pointer'
              : 'not-allowed',
          fontSize: '16px',
          fontWeight: '500',
          transition: 'background-color 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          width: '100%',
        }}
        onMouseOver={e => {
          if (projectId.trim() && !isArchivingEncordAssets) {
            e.currentTarget.style.backgroundColor = '#40a9ff'
          }
        }}
        onMouseOut={e => {
          if (projectId.trim() && !isArchivingEncordAssets) {
            e.currentTarget.style.backgroundColor = '#1890ff'
          }
        }}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="16" y1="8" x2="8" y2="8"></line>
          <line x1="16" y1="16" x2="8" y2="16"></line>
          <line x1="10" y1="12" x2="3" y2="12"></line>
          <line x1="21" y1="12" x2="14" y2="12"></line>
        </svg>
        {isArchivingEncordAssets ? 'Archiving...' : 'Archive Assets'}
      </button>

      {isArchivingEncordAssets ? (
        <div>
          <Spin tip="Archiving assets in Encord..." />
        </div>
      ) : null}
    </div>
  )
}

const getArchiveEncordAssetsLoadingSelector = createLoadingSelector([
  archiveEncordAssets.request,
])

const getArchiveEncordAssetsSuccessSelector = createSuccessSelector([
  archiveEncordAssets.success,
])

const mapStateToProps = (state: RootState) => ({
  isArchivingEncordAssets: getArchiveEncordAssetsLoadingSelector(state),
  isEncordAssetsArchived: getArchiveEncordAssetsSuccessSelector(state),
})

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      archiveEncordAssets: archiveEncordAssetsFlow,
    },
    dispatch,
  )

export default connect(mapStateToProps, mapDispatchToProps)(ArchiveEncordAssets)
